import { useReducer, useLayoutEffect, useEffect, useRef, useCallback } from 'react';
import { GoogleOptionsService } from '#service/autocomplete-service/service';
import { requestStatuses as statuses } from '#helper';

const defaultState = {
  status: statuses.idle,
  options: [],
  error: null
};

const actionTypes = Object.freeze({
  fetchStarted: 'fetchStarted',
  fetchSucceeded: 'fetchSucceeded',
  fetchFailed: 'fetchFailed',
  clear: 'clear'
});

const reducer = (state, action) => {
  switch (action.type) {
    case actionTypes.fetchStarted:
      return { ...state, status: statuses.loading, error: null };
    case actionTypes.fetchSucceeded:
      return { ...state, status: statuses.succeeded, options: action.payload };
    case actionTypes.fetchFailed:
      return { ...state, status: statuses.failed, options: [], error: action.error };
    case actionTypes.clear:
      return defaultState;
    default:
      return state;
  }
};

const useGoogleOptionsService = () => {
  const [state, dispatch] = useReducer(reducer, defaultState);
  const isMounted = useRef(true);
  const googleOptionsService = useRef(null);
  const abortController = useRef(new AbortController());

  useLayoutEffect(() => {
    if (!googleOptionsService.current && window.google) {
      googleOptionsService.current = new GoogleOptionsService(
        new window.google.maps.places.AutocompleteService(),
        new window.google.maps.places.PlacesService(document.createElement('div'))
      );
    }

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => () => {
    abortController.current.abort();
  }, []);

  const fetchOptions = useCallback(async (value, debounceTime = 500) => {
    try {
      abortController.current.abort();
      dispatch({ type: actionTypes.fetchStarted });
      abortController.current = new AbortController();
      const { signal } = abortController.current;
      await new Promise(res => {
        setTimeout(() => {
          res();
        }, debounceTime);
      });
      const newOptions = await googleOptionsService.current?.getOptions(value, { signal });
      if (isMounted.current) {
        dispatch({
          type: actionTypes.fetchSucceeded,
          payload: newOptions ?? []
        });
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        return;
      }
      if (isMounted.current) {
        dispatch({
          type: actionTypes.fetchFailed,
          error: e
        });
      }
    }
  }, []);

  return { ...state, fetchOptions };
};

export default useGoogleOptionsService;
